import { object } from 'yup'

import { PersistentState } from '../persistentState/type'
import { version } from '../version'
import { stepPages } from 'directFlow/types'

const localStorageKey = (quoteId: string) => `${quoteId}_DIRECT`

export enum LocalStorageErrorType {
  NOT_FOUND = 'NOT_FOUND',
  INVALID = 'INVALID',
  INCOMPATIBLE = 'INCOMPATIBLE',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pageStateShape = stepPages.reduce<Record<string, any>>((acc, pageKey) => {
  acc[pageKey] = object()
  return acc
}, {})

const persistentStateSchema = object({
  quoteGeneralInfo: object().required(),
  pageState: object(pageStateShape).noUnknown().required().strict(),
  meta: object().required(),
})

const isValidStateShape = (state: Record<string, unknown>): state is PersistentState => {
  return persistentStateSchema.isValidSync(state)
}

const isObject = (value: unknown): value is { [key: string]: unknown } => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

type LocalStorageFunc = (quoteId: string) => {
  get: () => PersistentState | { errorType: LocalStorageErrorType }
  sync: (state: PersistentState) => void
  clear: VoidFunction
}

export const LocalStorage: LocalStorageFunc = (quoteId) => {
  const key = localStorageKey(quoteId)

  return {
    get: () => {
      const stateFromLocalStorage = localStorage.getItem(key)

      if (stateFromLocalStorage === null) {
        return { errorType: LocalStorageErrorType.NOT_FOUND }
      }

      let localStorageObject

      try {
        localStorageObject = JSON.parse(stateFromLocalStorage)
      } catch {
        return { errorType: LocalStorageErrorType.INVALID }
      }

      if (!isObject(localStorageObject)) {
        return { errorType: LocalStorageErrorType.INVALID }
      }

      if (!isValidStateShape(localStorageObject)) {
        return { errorType: LocalStorageErrorType.INVALID }
      }

      if (localStorageObject.meta.version !== version) {
        return { errorType: LocalStorageErrorType.INCOMPATIBLE }
      }

      return localStorageObject
    },
    sync: (state) => localStorage.setItem(key, JSON.stringify(state)),
    clear: () => localStorage.removeItem(key),
  }
}
